import { useCallback } from "react";
import { usePostHog } from "posthog-react-native";

// Captures what location a person asked the cost estimate for, separately from
// what location the estimate could actually use. Comparing intent against the
// person's real coarse region relies on PostHog's built-in server-side GeoIP
// enrichment ($geoip_city_name, $geoip_subdivision_1_code) on the same event,
// so no IP-derived data is collected or retained client-side.
type EstimateLocationIntentEvent = {
  selectionSource: "suggested" | "current_location" | "search";
  requestedLocation: string;
  normalizedLocation: string | null;
  estimateLocation: string | null;
  // exact: a supported estimate location; nearest_fallback: priced through the
  // closest supported metro; country_estimate: priced at the national level;
  // unsupported: no estimate available for the country.
  matchType: "exact" | "nearest_fallback" | "country_estimate" | "unsupported";
};

export function useEstimateLocationIntent() {
  const posthog = usePostHog();

  const capture = useCallback(
    ({
      selectionSource,
      requestedLocation,
      normalizedLocation,
      estimateLocation,
      matchType,
    }: EstimateLocationIntentEvent) => {
      posthog.capture("cost_estimate_location_requested", {
        selection_source: selectionSource,
        requested_location: requestedLocation,
        normalized_location: normalizedLocation,
        estimate_location: estimateLocation,
        match_type: matchType,
        requested_matches_estimate:
          estimateLocation != null && requestedLocation === estimateLocation,
      });
    },
    [posthog],
  );

  return { capture };
}
